import type {Canvas, CanvasEvents, FabricObject, TPointerEvent} from '@postermywall/fabricjs-2';
import {ActiveSelection} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {AlignmentGuidesOverlay} from './alignment-guides-overlay.class';
import type {OverlayViewport} from './alignment-guides-overlay.class';
import {formatPixelLengthInPosterUnits} from '@Libraries/poster-object.library';
import type {CanvasBounds, GuideFrame, LengthFormatter, ResizeDrift, ResizeSnapStep, SnapBox, SnapGuide, SnapPoint} from './alignment-guides.types';
import {DISTANCE_READOUT_KEY_NAME, EMPTY_GUIDE_FRAME, NO_RESIZE_DRIFT, SNAP_MARGIN_SCREEN_PX} from './alignment-guides.types';
import {isDistanceReadoutModifierHeld, isSnapSuspendModifierHeld} from './snap-modifiers.library';
import {canvasBoundsToGuides, measureToBounds, resolveEqualSpacing, resolveMoveSnap, toSnapBox} from './snap-geometry.library';
import {applyResizeSnap, removeDrift} from './resize-snap.library';

interface CandidateCache {
  target: FabricObject;
  points: SnapPoint[];
  boxes: SnapBox[];
}

/**
 * The poster editor's one snap resolver. On every move of the active object (a fabric `object:moving`) and
 * every step of a resize (the editor's own corner and side handles, via {@link snapResize}) it gathers the other
 * items' corners and centres plus the host guides (canvas edges and centre lines, bleed lines), lets the
 * geometry library decide, applies the nudge to the fabric object and hands the overlay what to draw.
 * Equal-spacing detection and the distance readout ride on the same pass.
 *
 * The distance readout also works without a drag: pressing Alt with an item selected shows its distance to
 * each canvas edge, releasing Alt hides it.
 */
export class AlignmentGuides {
  readonly #page: Page;
  readonly #canvas: Canvas;
  readonly #overlay: AlignmentGuidesOverlay;
  /** The other items' reference points, gathered once per drag: nothing else moves while one object is dragged. */
  #candidateCache: CandidateCache | null = null;
  /** The object the current drift belongs to; a drift never carries over to another object. */
  #resizeTarget: FabricObject | null = null;
  #resizeDrift: ResizeDrift = NO_RESIZE_DRIFT;
  /** The snap lines and gap pills of the move in progress, without the readout; null when no item is being dragged. */
  #moveFrame: GuideFrame | null = null;
  #isDistanceReadoutKeyHeld = false;
  readonly #boundOnMoving: (e: CanvasEvents['object:moving']) => void;
  readonly #boundOnMouseUp: () => void;
  readonly #boundOnKeyChange: (e: KeyboardEvent) => void;
  readonly #boundOnSelectionChange: () => void;
  readonly #boundOnWindowBlur: () => void;
  readonly #formatLength: LengthFormatter = (pixels) => formatPixelLengthInPosterUnits(this.#page.poster, pixels);

  constructor(page: Page, canvas: Canvas) {
    this.#page = page;
    this.#canvas = canvas;
    this.#overlay = new AlignmentGuidesOverlay(page.hashedID);
    this.#boundOnMoving = this.#onMoving.bind(this);
    this.#boundOnMouseUp = this.#onMouseUp.bind(this);
    this.#boundOnKeyChange = this.#onKeyChange.bind(this);
    this.#boundOnSelectionChange = this.#renderMoveFrame.bind(this);
    this.#boundOnWindowBlur = this.#onWindowBlur.bind(this);
    canvas.on('object:moving', this.#boundOnMoving);
    canvas.on('mouse:up', this.#boundOnMouseUp);
    canvas.on('selection:created', this.#boundOnSelectionChange);
    canvas.on('selection:updated', this.#boundOnSelectionChange);
    canvas.on('selection:cleared', this.#boundOnSelectionChange);
    window.addEventListener('keydown', this.#boundOnKeyChange);
    window.addEventListener('keyup', this.#boundOnKeyChange);
    window.addEventListener('blur', this.#boundOnWindowBlur);
  }

  public dispose(): void {
    this.#canvas.off('object:moving', this.#boundOnMoving);
    this.#canvas.off('mouse:up', this.#boundOnMouseUp);
    this.#canvas.off('selection:created', this.#boundOnSelectionChange);
    this.#canvas.off('selection:updated', this.#boundOnSelectionChange);
    this.#canvas.off('selection:cleared', this.#boundOnSelectionChange);
    window.removeEventListener('keydown', this.#boundOnKeyChange);
    window.removeEventListener('keyup', this.#boundOnKeyChange);
    window.removeEventListener('blur', this.#boundOnWindowBlur);
    this.#overlay.dispose();
  }

  /** One step of a resize; see {@link ResizeSnapStep}. */
  public snapResize({target, handle, isUniform, pointerEvent}: ResizeSnapStep): void {
    if (this.#resizeTarget !== target) {
      this.#resizeTarget = target;
      this.#resizeDrift = NO_RESIZE_DRIFT;
    }
    if (this.#isSnappingSuspended(pointerEvent)) {
      removeDrift(target, handle, this.#resizeDrift);
      this.#resizeDrift = NO_RESIZE_DRIFT;
      this.#overlay.clear();
      return;
    }
    const candidates = this.#getCandidates(target);
    const result = applyResizeSnap({target, handle, isUniform, candidates: candidates.points, guides: this.#getGuides(), margin: this.#getMargin(), drift: this.#resizeDrift});
    this.#resizeDrift = result.drift;
    this.#overlay.render({lines: result.lines, measurements: []}, this.#getOverlayViewport());
  }

  /** The resize handle was released; the snapped geometry is final. */
  public endResize(): void {
    this.#resizeTarget = null;
    this.#resizeDrift = NO_RESIZE_DRIFT;
    this.#onMouseUp();
  }

  #onMoving(e: CanvasEvents['object:moving']): void {
    const target = e.target;
    target.setCoords();
    this.#isDistanceReadoutKeyHeld = isDistanceReadoutModifierHeld(e.e);
    if (this.#isSnappingSuspended(e.e)) {
      this.#moveFrame = EMPTY_GUIDE_FRAME;
      this.#renderMoveFrame();
      return;
    }
    const candidates = this.#getCandidates(target);
    const guides = this.#getGuides();
    const margin = this.#getMargin();
    const frame: GuideFrame = {lines: [], measurements: []};

    const move = resolveMoveSnap({origins: this.#getReferencePoints(target), candidates: candidates.points, guides, margin});
    this.#translate(target, move.deltaX, move.deltaY);
    frame.lines.push(...move.lines);

    const lockedX = move.lines.some((line) => line.axis === 'x');
    const lockedY = move.lines.some((line) => line.axis === 'y');
    for (const [axis, locked] of [
      ['x', lockedX],
      ['y', lockedY],
    ] as const) {
      const spacing = resolveEqualSpacing({target: toSnapBox(target.getBoundingRect()), neighbours: candidates.boxes, axis, margin, locked, formatLength: this.#formatLength});
      if (spacing) {
        this.#translate(target, axis === 'x' ? spacing.delta : 0, axis === 'y' ? spacing.delta : 0);
        frame.measurements.push(...spacing.measurements);
      }
    }

    this.#moveFrame = frame;
    this.#renderMoveFrame();
  }

  #onMouseUp(): void {
    this.#candidateCache = null;
    this.#moveFrame = null;
    this.#renderMoveFrame();
  }

  /** Alt pressed or released: show or hide the readout at once, whether or not an item is being dragged. */
  #onKeyChange(e: KeyboardEvent): void {
    if (e.key !== DISTANCE_READOUT_KEY_NAME || this.#resizeTarget) {
      return;
    }
    const isHeld = e.type === 'keydown';
    if (isHeld === this.#isDistanceReadoutKeyHeld) {
      return;
    }
    this.#isDistanceReadoutKeyHeld = isHeld;
    this.#renderMoveFrame();
  }

  /** Switching windows with Alt down (Alt+Tab) never delivers its keyup. */
  #onWindowBlur(): void {
    this.#isDistanceReadoutKeyHeld = false;
    this.#renderMoveFrame();
  }

  /** The current move's lines and pills, plus the selected item's distance to the canvas edges while Alt is held. */
  #renderMoveFrame(): void {
    const frame = this.#moveFrame ?? EMPTY_GUIDE_FRAME;
    const target = this.#getDistanceReadoutTarget();
    const readout = target ? measureToBounds(toSnapBox(target.getBoundingRect()), this.#getCanvasBounds(), this.#formatLength) : [];
    this.#overlay.render({lines: frame.lines, measurements: [...frame.measurements, ...readout]}, this.#getOverlayViewport());
  }

  /** The selected item while Alt is held, unless its text is being edited: Option types characters there on a Mac. */
  #getDistanceReadoutTarget(): FabricObject | null {
    if (!this.#isDistanceReadoutKeyHeld) {
      return null;
    }
    const target = this.#canvas.getActiveObject();
    if (!target || ('isEditing' in target && target.isEditing)) {
      return null;
    }
    return target;
  }

  #getOverlayViewport(): OverlayViewport {
    // The controls layer the overlay lives in is laid out at the poster scale, like the item handles.
    return {zoom: this.#page.poster.scaling.scale, sceneWidth: this.#page.poster.width, sceneHeight: this.#page.poster.height};
  }

  #translate(target: FabricObject, deltaX: number, deltaY: number): void {
    if (deltaX === 0 && deltaY === 0) {
      return;
    }
    target.set({left: target.left + deltaX, top: target.top + deltaY});
    target.setCoords();
  }

  #isSnappingSuspended(pointerEvent: TPointerEvent): boolean {
    return isSnapSuspendModifierHeld(pointerEvent);
  }

  /** Snap reach is a fixed number of screen pixels, converted to scene units at the current zoom. */
  #getMargin(): number {
    return SNAP_MARGIN_SCREEN_PX / this.#canvas.getZoom();
  }

  #getReferencePoints(target: FabricObject): SnapPoint[] {
    return [...target.getCoords(), target.getCenterPoint()].map((point) => ({x: point.x, y: point.y}));
  }

  #getCandidates(target: FabricObject): CandidateCache {
    if (this.#candidateCache?.target !== target) {
      const objects = this.#getCandidateObjects(target);
      this.#candidateCache = {
        target,
        points: objects.flatMap((object) => this.#getReferencePoints(object)),
        boxes: objects.map((object) => toSnapBox(object.getBoundingRect())),
      };
    }
    return this.#candidateCache;
  }

  /** Every visible on-screen item other than the target and, for a multi-selection, its members. */
  #getCandidateObjects(target: FabricObject): FabricObject[] {
    const excluded = new Set<FabricObject>(target instanceof ActiveSelection ? [target, ...target.getObjects()] : [target]);
    return this.#canvas.getObjects().filter((object) => object.visible && object.isOnScreen() && this.#isForegroundItem(object) && !excluded.has(object));
  }

  /**
   * A page item other than a background. A background covers the canvas, so its edges and centre sit on the
   * canvas guides: snapping to it too drew a dashed item line on top of every solid canvas line.
   */
  #isForegroundItem(object: FabricObject): boolean {
    if (!object.__PMWID) {
      return false;
    }
    return this.#page.items.getItem(object.__PMWID)?.isBackground !== true;
  }

  #getGuides(): SnapGuide[] {
    return [...canvasBoundsToGuides(this.#getCanvasBounds()), ...this.#page.renderBleed.getSnapGuides(), ...this.#page.renderGrid.getSnapGuides()];
  }

  #getCanvasBounds(): CanvasBounds {
    return {left: 0, top: 0, width: this.#page.poster.width, height: this.#page.poster.height};
  }
}
